import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccountType } from "../../../../models/AccountData";
import DataSvc from "../../../../services/dataSvc";
import { OptionalRender } from "../../../Common/OptionalRender";

interface IEditableAISummaryProps {
  title: string;
  summary: string | undefined;
  facilityReportId: string;
  /** Key within location_facility_reports_report_data.data to patch */
  patchKey: string;
  isDownloading?: boolean;
}

export const EditableAISummary: React.FunctionComponent<
  IEditableAISummaryProps
> = ({ title, summary, facilityReportId, patchKey, isDownloading }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityReportsPage.facilityReportDetails.editableAISummary.${key}`);

  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(summary ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setText(summary ?? "");
    }
  }, [summary, isEditing]);

  useEffect(() => {
    if (isDownloading) {
      setIsEditing(false);
    }
  }, [isDownloading]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await DataSvc.patchFacilityReport(facilityReportId, {
        [patchKey]: { ai_summary: text },
      });
      await queryClient.invalidateQueries(["facilityReport", facilityReportId]);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="editable-ai-summary-header">
        <h2>{title}</h2>
        <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
          {!isEditing && !isDownloading && (
            <button
              className="edit-pencil-btn"
              type="button"
              onClick={() => setIsEditing(true)}
              aria-label={t("edit")}
            >
              <Pencil size={16} />
            </button>
          )}
        </OptionalRender>
      </div>
      {isEditing ? (
        <div className="editable-ai-summary-edit">
          <textarea
            className="ai-summary-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
          />
          <div className="editable-ai-summary-actions">
            <button
              className="btn btn-border"
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </button>
            <button
              className={`btn btn-blue ${isSubmitting ? "disabled" : ""}`}
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("submitting") : t("submit")}
            </button>
          </div>
        </div>
      ) : (
        summary && <p className="ai-summary">{summary}</p>
      )}
    </>
  );
};
